import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";

const SYNC_STATUS_BG = {
  synced: "bg-green-100",
  failed: "bg-red-100",
  pending: "bg-yellow-100",
};
const syncBg = (status) => SYNC_STATUS_BG[status] || "bg-zinc-100";

export default function Leads() {
  const [params] = useSearchParams();
  const presetLm = params.get("lead_magnet_id") || "";
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(presetLm);
  const [lms, setLms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = {};
      if (filter) q.lead_magnet_id = filter;
      if (search) q.search = search;
      const r = await api.get("/leads", { params: q });
      setLeads(r.data.leads);
    } catch (e) { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  useEffect(() => {
    api.get("/lead-magnets").then((r) => setLms(r.data.lead_magnets)).catch(() => {});
  }, []);

  const handleSearch = (e) => { e.preventDefault(); load(); };

  const exportCsv = () => {
    const token = localStorage.getItem("vibelead_token");
    const url = `${API_BASE}/leads/export${filter ? `?lead_magnet_id=${filter}` : ""}`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "vibelead-leads.csv";
        a.click();
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <AppShell>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-cap mb-2">// All your leads</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Leads</h1>
        </div>
        <button onClick={exportCsv} className="btn-brutal" data-testid="export-csv-btn"><Download size={16} /> Export CSV</button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="input-brutal !w-auto" data-testid="filter-lm">
          <option value="">All Campaigns</option>
          {lms.map((lm) => (<option key={lm.id} value={lm.id}>{lm.title}</option>))}
        </select>
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[260px]">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email or name…" className="input-brutal flex-1" data-testid="search-input" />
          <button type="submit" className="btn-brutal-outline !px-3" data-testid="search-btn"><Search size={16} /></button>
        </form>
      </div>

      <div className="border-2 border-black overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b-2 border-black">
            <tr>
              <th className="text-left p-3 font-display font-bold">Email</th>
              <th className="text-left p-3 font-display font-bold">Name</th>
              <th className="text-left p-3 font-display font-bold">Phone</th>
              <th className="text-left p-3 font-display font-bold">Campaign</th>
              <th className="text-left p-3 font-display font-bold">Sync</th>
              <th className="text-left p-3 font-display font-bold">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-10 text-center text-zinc-500">Loading…</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={6} className="p-10 text-center text-zinc-500">No leads yet. Publish a lead magnet to start collecting.</td></tr>
            ) : leads.map((l) => (
              <tr key={l.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`lead-row-${l.id}`}>
                <td className="p-3 font-mono text-xs">{l.email}</td>
                <td className="p-3">{l.name || <span className="text-zinc-400">—</span>}</td>
                <td className="p-3">{l.phone || <span className="text-zinc-400">—</span>}</td>
                <td className="p-3">{l.lead_magnet_title}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 text-xs font-bold border-2 border-black ${syncBg(l.sync_status)}`}>{(l.sync_status || "—").toUpperCase()}</span>
                </td>
                <td className="p-3 text-zinc-500 text-xs">{(l.created_at || "").slice(0, 19).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-sm text-zinc-500" data-testid="leads-count">Showing {leads.length} leads</div>
    </AppShell>
  );
}
